// eslint-disable-next-line no-restricted-imports
import { Currency, Token } from '@kodiak-finance/sdk-core'
import SearchBanner from 'components/modules/IslandList/SearchBanner'
import { Spinner, SpinnerWithText } from 'components/Spinner'
import { WarningContent, WarningSection } from 'components/swap/SwapDetailsDropdown'
import { SupportedChainId } from 'config/chains'
import { PRE_TGE_XKDK_ADDRESSES, XKDK_ADDRESSES } from 'constants/addresses'
import { unsupportedSwapTokens } from 'constants/unsupportedSwapTokens'
import { useConnection } from 'hooks/useConnection'
import useDebounce from 'hooks/useDebounce'
import { useOnClickOutside } from 'hooks/useOnClickOutside'
import { useRecentTokens } from 'hooks/useRecentTokens'
import useTheme from 'hooks/useTheme'
import useToggle from 'hooks/useToggle'
import { useGetTokenPrices } from 'hooks/useTokenPrice'
import { useTrendingTokens } from 'hooks/useTrendingTokens'
import useNativeCurrency from 'lib/hooks/useNativeCurrency'
import { ChangeEvent, KeyboardEvent, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle } from 'react-feather'
import AutoSizer from 'react-virtualized-auto-sizer'
import { Text } from 'rebass'
import { useAllTokenBalancesFast, useCurrencyBalance } from 'state/connection/hooks'
import styled from 'styled-components'

import { useAllTokens, useFilteredSortedTokens, useIsUserAddedToken, useSearchToken } from '../../hooks/Tokens'
import { CloseIcon, ThemedText } from '../../theme'
import { isAddress } from '../../utils'
import Column, { AutoColumn } from '../Column'
import { RowBetween } from '../Row'
import CommonBases from './CommonBases'
import CurrencyList, { isSectionHeader } from './CurrencyList'
import ImportRow from './ImportRow'
import { SearchIcon, SearchInput, SearchInputWrapper, Separator } from './styleds'
import TokenListSkeleton from './TokenListSkeleton'
import TokenTabs, { ALL_TOKENS_FILTER, TokenFilter, tokenFiltersForChain } from './TokenTabs'
import { useTokenSelectorItems } from './useTokenSelectorItems'

const ContentWrapper = styled(Column)`
  width: 100%;
  // The horizontally scrolling tab row must not widen the dialog.
  min-width: 0;
  flex: 1 1;
  position: relative;
`

const HeaderColumn = styled(AutoColumn)`
  grid-template-columns: minmax(0, 1fr);
`

// Reaches into the modal's 24px right padding so the list scrollbar sits by the edge.
const ListBleed = styled.div`
  flex: 1;
  margin-right: -16px;
`

const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

interface CurrencySearchProps {
  isOpen: boolean
  onDismiss: () => void
  selectedCurrency?: Currency | null
  onCurrencySelect: (currency: Currency) => void
  otherSelectedCurrency?: Currency | null
  showCommonBases?: boolean
  showCurrencyAmount?: boolean
  disableNonToken?: boolean
  allowedCurrencies?: string[]
  allowNativeToken?: boolean
  showImportView: () => void
  setImportToken: (token: Token) => void
  preferredCurrencies?: Currency[]
  showCategories?: boolean
}

export function CurrencySearch({
  selectedCurrency,
  onCurrencySelect,
  otherSelectedCurrency,
  showCommonBases,
  showCurrencyAmount,
  disableNonToken,
  allowedCurrencies,
  allowNativeToken = true,
  onDismiss,
  isOpen,
  showImportView,
  setImportToken,
  preferredCurrencies,
  showCategories = true,
}: CurrencySearchProps) {
  const { account, chainId } = useConnection()
  const theme = useTheme()

  const [tokenLoaderTimerElapsed, setTokenLoaderTimerElapsed] = useState(false)

  const fixedList = useRef()

  const [searchQuery, setSearchQuery] = useState<string>('')
  const debouncedQuery = useDebounce(searchQuery, 200)

  const tokenFilters = useMemo(() => tokenFiltersForChain(chainId), [chainId])
  const [selectedFilter, setFilter] = useState<TokenFilter>(ALL_TOKENS_FILTER)
  // A tab picked on another chain may not exist on this one.
  const filter = tokenFilters.includes(selectedFilter) ? selectedFilter : ALL_TOKENS_FILTER
  const selectedCategories = useMemo(() => (filter === ALL_TOKENS_FILTER ? [] : [filter]), [filter])
  // A fixed set of allowed tokens keeps the plain list without tabs or sections.
  const isRestricted = Boolean(allowedCurrencies?.length)

  const preTgeXkdkAddress = PRE_TGE_XKDK_ADDRESSES[chainId as SupportedChainId]
  const xkdkAddress = XKDK_ADDRESSES[chainId as SupportedChainId]
  const allowedCurrencySet = useMemo(() => {
    return new Set((allowedCurrencies ?? []).map((address) => address.toLowerCase()))
  }, [allowedCurrencies])
  const tokensToExclude = useMemo(() => {
    return [preTgeXkdkAddress, xkdkAddress]
      .filter(Boolean)
      .filter((address) => !allowedCurrencySet.has(address.toLowerCase()))
  }, [preTgeXkdkAddress, xkdkAddress, allowedCurrencySet])
  const allTokens = useAllTokens([...unsupportedSwapTokens, ...tokensToExclude])
  const tokenList = useMemo(() => Object.values(allTokens), [allTokens])

  // if they input an address, use it
  const isAddressSearch = isAddress(debouncedQuery)

  const {
    token: searchToken,
    isLoading: isLoadingSearchToken,
    error: searchingTokenError,
  } = useSearchToken(debouncedQuery)

  const isSearchedTokenAlreadyAddedByUser = useIsUserAddedToken(searchToken)

  const [balances, balancesIsLoading] = useAllTokenBalancesFast()
  // Only the plain restricted list is ordered by balance.
  const isWaitingForBalances = isRestricted && balancesIsLoading && !tokenLoaderTimerElapsed

  // Priced over every listed token, matching the app-start prefetch, so the cache entry is shared.
  const pricedTokens = useAllTokens()
  const tokenAddresses = useMemo(() => Object.values(pricedTokens).map((token) => token.address), [pricedTokens])
  const { prices: tokenPrices } = useGetTokenPrices(tokenAddresses)

  const pricesLoading = useMemo(() => {
    if (tokenPrices === null) return true
    if (!Array.isArray(tokenPrices)) return true
    if (tokenPrices.length !== tokenAddresses.length) return true
    return false
  }, [tokenPrices, tokenAddresses.length])

  const tokenPriceMap = useMemo(() => {
    if (!tokenPrices || !Array.isArray(tokenPrices)) return {}

    const priceMap: { [address: string]: number | null } = {}
    tokenAddresses.forEach((address, index) => {
      priceMap[address] = tokenPrices[index] !== undefined ? tokenPrices[index] : null
    })
    return priceMap
  }, [tokenAddresses, tokenPrices])

  const { filteredSortedTokens, filteredSortedTokensWithNative, useSmartFilter, hasMultipleCategoryResults } =
    useFilteredSortedTokens({
      tokens: tokenList,
      selectedCategories,
      balances: {
        data: balances,
        isLoading: balancesIsLoading,
      },
      tokenPrices: {
        data: tokenPrices,
        isLoading: pricesLoading,
      },
      allowNativeToken,
      debouncedQuery,
      waitForBalances: isWaitingForBalances,
      allowedCurrencies,
      preferredCurrencies,
      showCommonBases,
    })

  const native = useNativeCurrency()
  const selectableNative = !disableNonToken && allowNativeToken ? native : undefined
  const nativeBalance = useCurrencyBalance(account ?? undefined, native)
  const { recentKeys, addRecent, clearRecent } = useRecentTokens(chainId)
  const { stats: marketStats, ranks: trendingRanks, isLoading: trendingLoading } = useTrendingTokens(chainId)

  const filteredCurrencies = disableNonToken ? filteredSortedTokens : filteredSortedTokensWithNative
  const sectionedItems = useTokenSelectorItems({
    query: debouncedQuery,
    pool: filteredCurrencies,
    nativeBalance,
    balances,
    prices: tokenPriceMap,
    marketStats,
    trendingRanks,
    trendingLoading,
    recentKeys,
    onClearRecent: clearRecent,
  })
  const items = isRestricted ? filteredCurrencies : sectionedItems
  // Persisted queries make later loads instant; the skeleton only covers a first
  // visit, before trending tokens or any balance have ever been fetched.
  const hasNoBalancesYet = balancesIsLoading && Object.keys(balances).length === 0
  const isFirstLoad =
    !isRestricted && !debouncedQuery && !tokenLoaderTimerElapsed && (trendingLoading || hasNoBalancesYet)
  const showSkeleton = isWaitingForBalances || isFirstLoad
  const firstCurrency = useMemo(() => items.find((item): item is Currency => !isSectionHeader(item)), [items])
  const listedCurrencies = useMemo(() => items.filter((item): item is Currency => !isSectionHeader(item)), [items])

  const isTokenSearchedByAddressInDefaultList = useMemo(() => {
    if (!searchToken || !isAddressSearch) return false
    return Object.values(allTokens).some((token) => token.address.toLowerCase() === searchToken.address.toLowerCase())
  }, [searchToken, allTokens, isAddressSearch])

  const handleCurrencySelect = useCallback(
    (currency: Currency) => {
      addRecent(currency)
      onCurrencySelect(currency)
      onDismiss()
    },
    [addRecent, onDismiss, onCurrencySelect]
  )

  // clear the input on open
  useEffect(() => {
    if (isOpen) setSearchQuery('')
  }, [isOpen])

  // manage focus on modal show
  const inputRef = useRef<HTMLInputElement>()
  const handleInput = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value
    const checksummedInput = isAddress(input)
    setSearchQuery(checksummedInput || input)
    fixedList.current?.scrollTo(0)
  }, [])

  const handleEnter = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        const s = debouncedQuery.toLowerCase().trim()
        if (selectableNative && s === selectableNative.symbol?.toLowerCase()) {
          handleCurrencySelect(selectableNative)
        } else if (firstCurrency) {
          if (
            firstCurrency.symbol?.toLowerCase() === debouncedQuery.trim().toLowerCase() ||
            listedCurrencies.length === 1
          ) {
            handleCurrencySelect(firstCurrency)
          }
        }
      }
    },
    [debouncedQuery, selectableNative, firstCurrency, listedCurrencies.length, handleCurrencySelect]
  )

  // menu ui
  const [open, toggle] = useToggle(false)
  const node = useRef<HTMLDivElement>()
  useOnClickOutside(node, open ? toggle : undefined)

  // Timeout token loader after 3 seconds to avoid hanging in a loading state.
  useEffect(() => {
    const tokenLoaderTimer = setTimeout(() => {
      setTokenLoaderTimerElapsed(true)
    }, 3000)
    return () => clearTimeout(tokenLoaderTimer)
  }, [])

  return (
    <ContentWrapper>
      <HeaderColumn gap="20px">
        <RowBetween>
          <Text fontWeight={700} fontSize={18}>
            <>Select a token</>
          </Text>
          <CloseIcon onClick={onDismiss} />
        </RowBetween>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {!allowedCurrencies?.length && (
            <SearchInputWrapper>
              <SearchIcon />
              <SearchInput
                type="text"
                id="token-search-input"
                placeholder="Search by name, symbol or address"
                autoComplete="off"
                autoFocus
                value={searchQuery}
                ref={inputRef as RefObject<HTMLInputElement>}
                onChange={handleInput}
                onKeyDown={handleEnter}
              />
            </SearchInputWrapper>
          )}

          {showCommonBases && (
            <CommonBases
              chainId={chainId}
              onSelect={handleCurrencySelect}
              selectedCurrency={selectedCurrency}
              searchQuery={searchQuery}
              isAddressSearch={isAddressSearch}
              disableNonToken={disableNonToken}
              preferredCurrencies={preferredCurrencies}
            />
          )}

          {showCategories && <TokenTabs filters={tokenFilters} selected={filter} onSelect={setFilter} />}
          {useSmartFilter && searchQuery && (
            <SearchBanner
              hasResults={!!filteredSortedTokensWithNative.length}
              size="small"
              hasMultipleCategoryResults={hasMultipleCategoryResults}
            />
          )}
          {/* The tab row draws its own bottom border. */}
          {(!showCategories || (useSmartFilter && searchQuery)) && <Separator style={{ margin: 0 }} />}
        </div>
      </HeaderColumn>
      {!isLoadingSearchToken &&
      searchToken &&
      !isSearchedTokenAlreadyAddedByUser &&
      !isTokenSearchedByAddressInDefaultList ? (
        <Column style={{ padding: '20px 0', height: '100%' }}>
          <ImportRow
            token={searchToken}
            showImportView={showImportView}
            setImportToken={setImportToken}
            needImgFallbackOnLoading
          />
        </Column>
      ) : showSkeleton ? (
        <ListBleed>
          <TokenListSkeleton />
        </ListBleed>
      ) : items.length > 0 ? (
        <ListBleed>
          <AutoSizer disableWidth>
            {({ height }) => (
              <CurrencyList
                // Remounting per filter and query starts each view scrolled to the top.
                key={`${filter}:${debouncedQuery}`}
                height={height}
                currencies={items}
                onCurrencySelect={handleCurrencySelect}
                otherCurrency={otherSelectedCurrency}
                selectedCurrency={selectedCurrency}
                fixedListRef={fixedList}
                showImportView={showImportView}
                setImportToken={setImportToken}
                showCurrencyAmount={showCurrencyAmount}
                isLoading={false}
                searchQuery={searchQuery}
                isAddressSearch={isAddressSearch}
                tokenPriceMap={tokenPriceMap}
                showMarketCard
              />
            )}
          </AutoSizer>
        </ListBleed>
      ) : (
        <Column style={{ padding: '20px', height: '100%' }}>
          {isLoadingSearchToken ? (
            <SpinnerWithText>
              <Spinner />
              <ThemedText.DeprecatedBody color="white">Searching token...</ThemedText.DeprecatedBody>
            </SpinnerWithText>
          ) : (
            <DetailsWrapper>
              <ThemedText.DeprecatedMain color={theme.deprecated_text3} textAlign="center" mb="20px">
                <>No results found.</>
              </ThemedText.DeprecatedMain>
              {searchingTokenError && (
                <WarningSection>
                  <WarningContent>
                    <AlertTriangle size={16} style={{ flexShrink: 0 }} />
                    <ErrorWrapper>
                      <Text fontWeight={500}>Failed to import the token:</Text>
                      <Text fontWeight={300} fontSize="10px">
                        {searchingTokenError}
                      </Text>
                    </ErrorWrapper>
                  </WarningContent>
                </WarningSection>
              )}
            </DetailsWrapper>
          )}
        </Column>
      )}
    </ContentWrapper>
  )
}
